import { COORDINATE_SYSTEM, Layer } from '@deck.gl/core';
import { ColorVector, TagBorderMode } from '@fe/core';
import { IFlightItem } from '../flights/types';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import AnimatedTagLayer from './layers/text-tag/animated-tag-layer';
import { HidableTextLayer } from './layers/text-tag/hiddable-text-layer';
import { AnimatedLeaderLineLayer } from './layers/text-tag/leader-lines/animated-leader-line-layer';
import { LeaderLineLayer } from './layers/text-tag/leader-lines/leader-line-layer';
import { TagLeaderItem } from './types';

export class DeckTags extends DeckLayerCreator {
    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
    }

    createLayers(data: IFlightItem[], leaders: TagLeaderItem[] | undefined): Layer[] {
        if (!this.props?.flights.active.visible && data.length > 0) {
            return [];
        }

        const layers = [] as Layer[];

        // Leader lines
        if (this.props?.flights.active.visible && !this.props!.flights.active.tags.hideLeader) {
            const { tags } = this.props!.flights.active;
            const { leaderColor } = tags;

            const leaderLayerProps: Partial<LeaderLineLayer<TagLeaderItem>['props']> = {
                data: leaders,
                coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
                getColor: leaderColor,
                getWidth: 1,
                getPixelOffset: (d) => [d.offset[0] - 4, d.offset[1] - 4],
                getIsHidden: (d) => this.getIsHidden(d.flight),
                updateTriggers: {
                    all: [this.autoArrangeVersion],
                    getIsHidden: this.getIsHiddenUpdateTrigger
                },
                parameters: this.plugin.getRenderingParams()
            };
            layers.push(
                this.isCoastMode
                    ? new AnimatedLeaderLineLayer<TagLeaderItem>({
                          ...leaderLayerProps,
                          id: 'animated-tag-leaders',
                          positionTexture: this.plugin.positionTexture,
                          getId: (x) => x.flight.id
                      })
                    : new LeaderLineLayer<TagLeaderItem>({
                          ...leaderLayerProps,
                          id: 'tag-leaders',
                          getPosition: (d) => this.getFlightPosition(d.flight)
                      })
            );
        }

        const { tags } = this.props!.flights.active;
        const { outlineMode, outlineColor, color, background, drawBackground } = tags;
        const tagsLayerProps: Partial<HidableTextLayer<IFlightItem>['props']> = {
            data,
            pickable: false,
            coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
            getText: (d) => d.tagContent ?? '',
            getPixelOffset: (d) => [d.tagOffsetX, d.tagOffsetY],
            getSize: 10,
            getAngle: 0,
            billboard: true,
            backgroundPadding: [4, 4],
            getTextAnchor: 'start',
            getColor: (d) => this.getTagColor(d),
            getBorderWidth: outlineMode === TagBorderMode.none ? 0 : 1,
            getBorderColor: outlineMode === TagBorderMode.other ? outlineColor : color,
            getBackgroundColor: background,
            getAlignmentBaseline: 'top',
            fontFamily: 'helvetica',
            getIsHidden: (d) => this.getIsHidden(d),
            fontSettings: {
                sdf: false,
                smoothing: 0.5
            },
            characterSet: 'auto',
            background: drawBackground,
            updateTriggers: {
                all: [this.flightsVersion],
                getColor: [this.propsVersion],
                getIsHidden: this.getIsHiddenUpdateTrigger,
                getPixelOffset: [this.autoArrangeVersion]
            },
            parameters: this.plugin.getRenderingParams()
        };

        layers.push(
            this.isCoastMode
                ? new AnimatedTagLayer({
                      ...tagsLayerProps,
                      id: 'animated-tags',
                      getId: (d) => d.id,
                      positionTexture: this.plugin.positionTexture
                  })
                : new HidableTextLayer<IFlightItem>({
                      ...tagsLayerProps,
                      id: 'tags',
                      getPosition: (d) => this.getFlightPosition(d)
                  })
        );

        return layers;
    }

    private getTagColor(item: IFlightItem): ColorVector {
        return item.filterTagColorSameAsPlane && item.filterColor ? item.filterColor : this.props!.flights.active.tags.color;
    }
}
